/**
 * E-Signature Component for Accountant Portal
 * Enhanced version with better UI and functionality
 */

import React, { useRef, useState, useEffect } from 'react';
import SignaturePad from 'signature_pad';
import axios from '@/lib/http';
import { FileSignature, Check, Pencil, Keyboard, RotateCcw, AlertTriangle } from 'lucide-react';
import { getAuthHeaders } from '../lib/authHeaders';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface Signature {
  id: string;
  report_id: string;
  accountant_id: string | null;
  accountant_name: string;
  signature_data_url: string;
  signed_at: string;
  version_id?: string;
  notes?: string;
  signer_type?: string;
}

interface ESignatureProps {
  reportId: string;
  getAuthHeader: () => any;
  canSign: boolean;
}

export default function ESignature({ reportId, getAuthHeader, canSign }: ESignatureProps) {
  const [signatures, setSignatures] = useState<Signature[]>([]);
  const [loading, setLoading] = useState(true);
  const [showSignModal, setShowSignModal] = useState(false);
  const [signing, setSigning] = useState(false);
  const [notes, setNotes] = useState('');
  
  // Signature pad
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const signaturePadRef = useRef<SignaturePad | null>(null);
  
  // Signature method tabs
  const [activeTab, setActiveTab] = useState<'draw' | 'type'>('draw');
  const [typedName, setTypedName] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Additional fields (will be populated from profile)
  const [title, setTitle] = useState('Forensic Accountant');
  const [credentials, setCredentials] = useState('');
  const [attestation, setAttestation] = useState(
    'I hereby certify that this report is true and accurate to the best of my knowledge and professional judgment.'
  );
  
  // Profile data
  const [profileData, setProfileData] = useState<any>(null);
  const [accountantName, setAccountantName] = useState('');

  useEffect(() => {
    fetchSignatures();
    fetchAccountantProfile();
  }, [reportId]);

  // Fetch accountant profile to pre-fill credentials
  const fetchAccountantProfile = async () => {
    try {
      // Get basic accountant info from localStorage
      const storedAccountant = localStorage.getItem('accountant_profile');
      if (storedAccountant) {
        const accountant = JSON.parse(storedAccountant);
        setAccountantName(accountant.full_name || '');
        setTypedName(accountant.full_name || '');
      }

      // Try to fetch full profile with credentials
      const response = await axios.get(
        `${BACKEND_URL}/api/v1/accountant-profile/me`,
        { headers: await getAuthHeaders() }
      );
      
      setProfileData(response.data);
      
      // Build credentials string from profile
      const certs = response.data.certifications || [];
      if (certs.length > 0) {
        setCredentials(certs.join(', '));
      } else if (response.data.cpa_license_number) {
        setCredentials('CPA');
      }
      
      // Use full name from profile if available
      if (response.data.full_name) {
        setAccountantName(response.data.full_name);
        setTypedName(response.data.full_name);
      }
    } catch (error: any) {
      console.log('Could not load full profile, using basic info');
      // This is okay - profile might not be complete yet
    }
  };

  // Initialize signature pad when modal opens
  useEffect(() => {
    if (showSignModal && canvasRef.current && !signaturePadRef.current) {
      signaturePadRef.current = new SignaturePad(canvasRef.current, {
        backgroundColor: 'rgb(255, 255, 255)',
        penColor: 'rgb(0, 0, 0)',
        minWidth: 0.5,
        maxWidth: 2.5
      });
    }

    return () => {
      if (signaturePadRef.current) {
        signaturePadRef.current.off();
      }
    };
  }, [showSignModal]);

  const fetchSignatures = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${BACKEND_URL}/api/v1/collaboration/reports/${reportId}/signatures`,
        { headers: await getAuthHeaders() }
      );
      setSignatures(response.data || []);
    } catch (err: any) {
      console.error('Failed to fetch signatures:', err);
      setSignatures([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
    }
  };

  const generateTypedSignature = (): string => {
    const canvas = document.createElement('canvas');
    canvas.width = 400;
    canvas.height = 150;
    const ctx = canvas.getContext('2d');
    
    if (ctx) {
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      ctx.fillStyle = 'black';
      ctx.font = '48px Brush Script MT, cursive';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(typedName, canvas.width / 2, canvas.height / 2);
    }
    
    return canvas.toDataURL('image/png');
  };

  const handleSign = async () => {
    try {
      setSigning(true);
      
      let signatureDataUrl: string;

      if (activeTab === 'draw') {
        if (!signaturePadRef.current || signaturePadRef.current.isEmpty()) {
          setValidationError('Please draw your signature first');
          setSigning(false);
          return;
        }
        signatureDataUrl = signaturePadRef.current.toDataURL('image/png');
      } else {
        if (!typedName.trim()) {
          setValidationError('Please enter your name');
          setSigning(false);
          return;
        }
        signatureDataUrl = generateTypedSignature();
      }

      // Sign the report
      await axios.post(
        `${BACKEND_URL}/api/v1/collaboration/reports/${reportId}/signatures`,
        {
          signature_data_url: signatureDataUrl,
          notes: notes || null
        },
        { headers: await getAuthHeaders() }
      );

      setSuccessMessage('Report signed successfully!');
      setShowSignModal(false);
      fetchSignatures();
      
      // Reset form
      setNotes('');
      setTypedName('');
      if (signaturePadRef.current) {
        signaturePadRef.current.clear();
      }
    } catch (err: any) {
      console.error('Failed to sign report:', err);
      if (err.response?.status === 403) {
        setValidationError('Sign-off signature is the attesting CPA\'s — you have view-only access.');
      } else {
        setValidationError(err.response?.data?.detail || 'Failed to sign report');
      }
    } finally {
      setSigning(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  if (loading) {
    return (
      <div className="rounded-[2px] p-6" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
        <p style={{ color: 'var(--color-text-muted)' }}>Loading signatures...</p>
      </div>
    );
  }

  // Check if already signed by the attesting CPA — an owner (in-house) test
  // signature must NOT count as a legal sign-off.
  const cpaSignatures = signatures.filter((s) => s.signer_type !== 'owner');
  const isSigned = cpaSignatures.length > 0;

  return (
    <div className="rounded-[2px] border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
      <div className="p-6">
        {successMessage && (
          <p className="text-sm mb-4" style={{ color: 'var(--color-verified)' }}>{successMessage}</p>
        )}

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <FileSignature className="w-6 h-6" style={{ color: 'var(--color-text-secondary)' }} />
            <h3 className="text-xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Digital Signature</h3>
          </div>
          {isSigned && (
            <span className="flex items-center gap-2 px-3 py-1 rounded-[2px] text-sm font-semibold" style={{ color: 'var(--color-verified)' }}>
              <Check className="w-4 h-4" />
              Signed
            </span>
          )}
        </div>

        {/* Show existing signatures */}
        {isSigned ? (
          <div className="space-y-4">
            {signatures.map((sig) => (
              <div key={sig.id} className="border rounded-[2px] p-4" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)' }}>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <img
                      src={sig.signature_data_url}
                      alt="Signature"
                      className="border rounded-[2px] p-2 bg-white max-h-24"
                      style={{ borderColor: 'var(--color-border)' }}
                    />
                  </div>
                  <div className="space-y-2" style={{ color: 'var(--color-verified)' }}>
                    <p className="text-sm">
                      <span className="font-semibold">Signed by:</span> {sig.accountant_name}
                      {sig.signer_type === 'owner' && (
                        <span className="ml-2 text-xs font-normal" style={{ color: 'var(--color-text-muted)' }}>
                          Owner (in-house test)
                        </span>
                      )}
                    </p>
                    {credentials && sig.signer_type !== 'owner' && (
                      <p className="text-sm">
                        <span className="font-semibold">Credentials:</span> {credentials}
                      </p>
                    )}
                    <p className="text-sm">
                      <span className="font-semibold">Date:</span> {formatDate(sig.signed_at)}
                    </p>
                    {sig.notes && (
                      <p className="text-sm">
                        <span className="font-semibold">Notes:</span> {sig.notes}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="border rounded-[2px] p-6" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}>
            <p className="mb-4" style={{ color: 'var(--color-text-muted)' }}>
              This report has not been digitally signed yet.
            </p>
            {canSign && (
              <button
                onClick={() => setShowSignModal(true)}
                className="px-6 py-3 rounded-[2px] font-semibold flex items-center gap-2 transition"
                style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
              >
                <FileSignature className="w-5 h-5" />
                Sign Report
              </button>
            )}
          </div>
        )}
      </div>

      {/* Signature Modal */}
      {showSignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="rounded-[2px] max-w-2xl w-full max-h-[90vh] overflow-y-auto" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
            {/* Modal Header */}
            <div className="p-6 rounded-t-[2px]" style={{ backgroundColor: 'var(--color-brand)' }}>
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold flex items-center gap-2" style={{ color: 'var(--color-on-brand)' }}>
                  <FileSignature className="w-6 h-6" />
                  Sign Report
                </h2>
                <button
                  onClick={() => setShowSignModal(false)}
                  className="text-2xl font-bold"
                  style={{ color: 'var(--color-on-brand)' }}
                >
                  ×
                </button>
              </div>
              <p className="mt-2 text-sm" style={{ color: 'var(--color-on-brand)' }}>
                Digitally sign this forensic accounting report
              </p>
            </div>

            <div className="p-6">
              {validationError && (
                <p className="text-sm mb-4" style={{ color: 'var(--color-blocker)' }}>{validationError}</p>
              )}

              {/* Tabs */}
              <div className="flex gap-2 mb-6">
                <button
                  onClick={() => { setActiveTab('draw'); setValidationError(null); }}
                  className="flex-1 py-2 px-4 rounded-[2px] font-medium transition inline-flex items-center justify-center gap-2"
                  style={
                    activeTab === 'draw'
                      ? { backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }
                      : { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)', border: '1px solid var(--color-border)' }
                  }
                >
                  <Pencil size={16} /> Draw Signature
                </button>
                <button
                  onClick={() => { setActiveTab('type'); setValidationError(null); }}
                  className="flex-1 py-2 px-4 rounded-[2px] font-medium transition inline-flex items-center justify-center gap-2"
                  style={
                    activeTab === 'type'
                      ? { backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }
                      : { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)', border: '1px solid var(--color-border)' }
                  }
                >
                  <Keyboard size={16} /> Type Signature
                </button>
              </div>

              {/* Draw Signature Tab */}
              {activeTab === 'draw' && (
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                    Draw your signature below:
                  </label>
                  <div className="border rounded-[2px] mb-4 bg-white" style={{ borderColor: 'var(--color-border)' }}>
                    <canvas
                      ref={canvasRef}
                      width={600}
                      height={200}
                      className="w-full rounded-[2px] touch-none"
                    />
                  </div>
                  <button
                    onClick={handleClear}
                    className="text-sm font-medium inline-flex items-center gap-1"
                    style={{ color: 'var(--color-text-secondary)' }}
                  >
                    <RotateCcw size={14} /> Clear Signature
                  </button>
                </div>
              )}

              {/* Type Signature Tab */}
              {activeTab === 'type' && (
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                    Type your full name:
                  </label>
                  <input
                    type="text"
                    value={typedName}
                    onChange={(e) => setTypedName(e.target.value)}
                    placeholder="John Doe"
                    className="w-full px-4 py-3 border rounded-[2px] focus:ring-2 focus:border-transparent"
                    style={{ borderColor: 'var(--color-border)' }}
                  />
                  <div className="mt-4 p-4 border rounded-[2px] bg-white h-32 flex items-center justify-center" style={{ borderColor: 'var(--color-border)' }}>
                    <p
                      className="text-5xl"
                      style={{ fontFamily: 'Brush Script MT, cursive', color: 'var(--color-text-primary)' }}
                    >
                      {typedName || 'Your Name Here'}
                    </p>
                  </div>
                </div>
              )}

              {/* Profile Information Display */}
              {profileData && (
                <div className="mt-6 p-4 border rounded-[2px]" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}>
                  <h4 className="text-sm font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
                    Your Profile Information
                  </h4>
                  <div className="text-sm space-y-1" style={{ color: 'var(--color-text-secondary)' }}>
                    <p><strong>Name:</strong> {accountantName || profileData.full_name}</p>
                    {credentials && <p><strong>Credentials:</strong> {credentials}</p>}
                    {profileData.cpa_license_number && (
                      <p><strong>CPA License:</strong> {profileData.cpa_license_number} ({profileData.license_state})</p>
                    )}
                  </div>
                </div>
              )}

              {/* Additional Fields */}
              <div className="mt-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                    Professional Title
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full px-4 py-2 border rounded-[2px] focus:ring-2 focus:border-transparent"
                    style={{ borderColor: 'var(--color-border)' }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                    Credentials {profileData && credentials ? '(from your profile)' : ''}
                  </label>
                  <input
                    type="text"
                    value={credentials}
                    onChange={(e) => setCredentials(e.target.value)}
                    placeholder="e.g., CPA, CFE, CFF"
                    className="w-full px-4 py-2 border rounded-[2px] focus:ring-2 focus:border-transparent"
                    style={{ borderColor: 'var(--color-border)' }}
                  />
                  {profileData && !credentials && (
                    <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                      Add certifications to your profile to auto-fill this field
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                    Attestation Statement
                  </label>
                  <textarea
                    value={attestation}
                    onChange={(e) => setAttestation(e.target.value)}
                    rows={3}
                    className="w-full px-4 py-2 border rounded-[2px] focus:ring-2 focus:border-transparent"
                    style={{ borderColor: 'var(--color-border)' }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                    Notes (Optional)
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={2}
                    placeholder="Add any notes or comments..."
                    className="w-full px-4 py-2 border rounded-[2px] focus:ring-2 focus:border-transparent"
                    style={{ borderColor: 'var(--color-border)' }}
                  />
                </div>
              </div>

              {/* Legal Notice */}
              <div className="mt-6 p-4 border rounded-[2px]" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}>
                <p className="text-sm flex items-start gap-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                  <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
                  <span><strong>Legal Notice:</strong> By signing this report, you certify that
                  the information contained herein is accurate and complete to the best of your
                  professional knowledge. This signature is legally binding.</span>
                </p>
              </div>

              {/* Actions */}
              <div className="mt-6 flex gap-3">
                <button
                  onClick={handleSign}
                  disabled={signing}
                  className="flex-1 py-3 px-6 rounded-[2px] font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-center gap-2"
                  style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                >
                  {signing ? (
                    'Signing...'
                  ) : (
                    <>
                      <FileSignature className="w-5 h-5" />
                      Sign Report
                    </>
                  )}
                </button>
                <button
                  onClick={() => setShowSignModal(false)}
                  className="px-6 py-3 rounded-[2px] font-semibold transition"
                  style={{ backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)', border: '1px solid var(--color-border)' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
